import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "lucide-react";
import { Product as SupabaseProduct } from "@/lib/product-service";

// Extended product interface to include Supabase specific fields
interface Product extends SupabaseProduct {
  image_url?: string;
  previous_price?: number;
  review_count?: number;
  supplier_name?: string;
  supplier_slug?: string;
}
import ProductCard from "@/polymet/components/product-card";
import { Link } from "react-router-dom";

interface FeaturedProductsProps {
  products: Product[];
  title?: string;
  description?: string;
  viewAllLink?: string;
  limit?: number;
  className?: string;
  loading?: boolean;
}

interface FeaturedProduct {
  id: string;
  name: string;
  price: number;
  image: string;
  slug: string;
  supplier_name: string;
  rating?: number;
  review_count?: number;
  condition?: string;
  location?: string;
}

export default function FeaturedProducts({
  products,
  title = "Featured Products",
  description,
  viewAllLink,
  limit = 4,
  className = "",
  loading = false,
}: FeaturedProductsProps) {
  // We're already getting featured products from the API, just limit them
  const featuredProducts = products.slice(0, limit);
  
  // Show loading state if data is still loading
  if (loading) {
    return (
      <Card className={`shadow-none ${className}`}>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">{title}</CardTitle>
          {description && <p className="text-muted-foreground">{description}</p>}
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
            <span className="ml-2 text-lg">Loading featured products...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`shadow-none ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-2xl font-bold">{title}</CardTitle>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </div>
        {viewAllLink && (
          <Link to={viewAllLink}>
            <Button variant="outline" className="hidden sm:flex items-center">
              View All
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {featuredProducts.map((product, index) => (
            <ProductCard
              key={product.id}
              id={product.id}
              name={product.name}
              condition={product.condition}
              price={product.price}
              previousPrice={product.previous_price}
              currency={product.currency || 'INR'}
              image={product.image_url || `/placeholder-product-${(index % 4) + 1}.jpg`}
              year={product.year}
              manufacturer={product.manufacturer}
              model={product.model}
              location={product.location}
              rating={product.rating || 0}
              reviewCount={product.review_count || 0}
              featured={product.featured}
              categoryId={product.category_id}
              subCategoryId={product.subcategory_id}
              productCategoryId={product.product_category_id}
              supplierName={product.supplier_name}
              supplierSlug={product.supplier_slug}
            />
          ))}
        </div>

        {featuredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">
              No featured products found
            </p>
          </div>
        )}

        {viewAllLink && (
          <div className="mt-6 text-center sm:hidden">
            <Link to={viewAllLink}>
              <Button variant="outline" className="w-full">
                View All Products
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
